import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Divider,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tab,
    Tabs,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    OutlinedInput,
    Alert,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Grid,
    Avatar,
    IconButton,
    CircularProgress,
    Stepper,
    Step,
    StepLabel,
    Switch,
    FormControlLabel,
    Snackbar
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WorkIcon from '@mui/icons-material/Work';
import LockResetIcon from '@mui/icons-material/LockReset';
import { theme } from '../../../theme';
import { superadminService } from '../../../services/superadmin.service';

const DAYS_OF_WEEK = [
    { id: 'monday', label: 'Lundi', salonKey: 'lundi' },
    { id: 'tuesday', label: 'Mardi', salonKey: 'mardi' },
    { id: 'wednesday', label: 'Mercredi', salonKey: 'mercredi' },
    { id: 'thursday', label: 'Jeudi', salonKey: 'jeudi' },
    { id: 'friday', label: 'Vendredi', salonKey: 'vendredi' },
    { id: 'saturday', label: 'Samedi', salonKey: 'samedi' },
    { id: 'sun', label: 'Dimanche', salonKey: 'dimanche' },
];

const DEFAULT_OPENING_HOURS = {
    'lundi': { 'ouverture': '09:00', 'fermeture': '19:00' },
    'mardi': { 'ouverture': '09:00', 'fermeture': '19:00' },
    'mercredi': { 'ouverture': '09:00', 'fermeture': '19:00' },
    'jeudi': { 'ouverture': '09:00', 'fermeture': '19:00' },
    'vendredi': { 'ouverture': '09:00', 'fermeture': '19:00' },
    'samedi': { 'ouverture': '09:00', 'fermeture': '19:00' },
    'dimanche': { 'ouverture': 'Fermé', 'fermeture': 'Fermé' }
};

const timeToMinutes = (timeValue) => {
    const [hours, minutes] = String(timeValue || '00:00').split(':').map(Number);
    return (hours * 60) + (minutes || 0);
};

const normalizeInterval = (startTime, endTime, { allowEqualAsFullDay = false } = {}) => {
    const start = timeToMinutes(startTime);
    let end = timeToMinutes(endTime);
    if (end < start || (allowEqualAsFullDay && end === start)) {
        end += 24 * 60;
    }
    return { start, end };
};

const isWithinSalonHours = (employeeStart, employeeEnd, salonOpen, salonClose) => {
    const salonInterval = normalizeInterval(salonOpen, salonClose, { allowEqualAsFullDay: true });
    const employeeInterval = normalizeInterval(employeeStart, employeeEnd);
    return (
        employeeInterval.start >= salonInterval.start
        && employeeInterval.end <= salonInterval.end
        && employeeInterval.end > employeeInterval.start
    );
};

const compareClockTimes = (left, right) => {
    const leftMinutes = timeToMinutes(left);
    const rightMinutes = timeToMinutes(right);
    if (rightMinutes <= leftMinutes) {
        return rightMinutes + (24 * 60) - leftMinutes;
    }
    return rightMinutes - leftMinutes;
};

const SalonTeam = ({ employees: initialEmployees, salonId }) => {
    const [employees, setEmployees] = useState(initialEmployees || []);
    const [salonData, setSalonData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [deleting, setDeleting] = useState(null);

    // Team Dialog State
    const [teamDialogOpen, setTeamDialogOpen] = useState(false);
    const [editingTeam, setEditingTeam] = useState(null);
    const [teamName, setTeamName] = useState('');
    const [teamDescription, setTeamDescription] = useState('');
    const [savingTeam, setSavingTeam] = useState(false);

    // Employee Creation Stepper State
    const [employeeDialogOpen, setEmployeeDialogOpen] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [activeTab, setActiveTab] = useState(0); // For edit mode
    const [editingEmployee, setEditingEmployee] = useState(null);
    const [employeeForm, setEmployeeForm] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        password: '',
        has_access: true,
        team_ids: [],
        schedules: DAYS_OF_WEEK.map(day => ({
            day_of_week: day.id,
            start_time: '09:00',
            end_time: '18:00',
            enabled: true
        }))
    });
    const [savingEmployee, setSavingEmployee] = useState(false);
    const [error, setError] = useState('');

    // Reset Password State
    const [resetOpen, setResetOpen] = useState(false);
    const [resetLoading, setResetLoading] = useState(false);
    const [resetError, setResetError] = useState('');
    const [resetSuccess, setResetSuccess] = useState('');
    const [resetUser, setResetUser] = useState(null);

    // Toast State
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

    const showToast = (message, severity = 'success') => {
        setToast({ open: true, message, severity });
    };

    const handleCloseToast = (event, reason) => {
        if (reason === 'clickaway') return;
        setToast({ ...toast, open: false });
    };

    const loadSalonData = async () => {
        try {
            const res = await superadminService.retrieveSalon(salonId);
            setSalonData(res.data);
            setEmployees(res.data.employees || []);
        } catch (error) {
            console.error("Failed to load salon data", error);
        }
    };

    useEffect(() => {
        if (salonId) {
            loadSalonData();
        }
    }, [salonId]);

    // Delete Confirmation State
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, type: null, id: null });

    const handleRequestDeleteEmployee = (memberId) => {
        setDeleteConfirm({ open: true, type: 'employee', id: memberId });
    };

    const handleRequestDeleteTeam = (teamId) => {
        setDeleteConfirm({ open: true, type: 'team', id: teamId });
    };

    const handleConfirmDelete = async () => {
        const { type, id } = deleteConfirm;
        if (!type || !id) return;

        try {
            if (type === 'employee') {
                setDeleting(id);
                await superadminService.deleteTeamMember(id);
                showToast('Employé retiré du salon.', 'success');
            } else if (type === 'team') {
                await superadminService.deleteTeam(salonId, id);
                showToast('Équipe supprimée.', 'success');
            }
            await loadSalonData();
        } catch (error) {
            console.error("Delete failed", error);
            showToast(
                type === 'employee' ? "Erreur lors de la suppression de l'employé" : "Erreur lors de la suppression de l'équipe",
                'error'
            );
        } finally {
            setDeleting(null);
            setDeleteConfirm({ open: false, type: null, id: null });
        }
    };

    const handleOpenTeamDialog = (team = null) => {
        if (team) {
            setEditingTeam(team);
            setTeamName(team.name);
            setTeamDescription(team.description || '');
        } else {
            setEditingTeam(null);
            setTeamName('');
            setTeamDescription('');
        }
        setTeamDialogOpen(true);
    };

    const handleSaveTeam = async () => {
        if (!teamName) return;
        try {
            setSavingTeam(true);
            const payload = { name: teamName, description: teamDescription };
            if (editingTeam) {
                await superadminService.updateTeam(salonId, editingTeam.team_id, payload);
                showToast('Équipe mise à jour.', 'success');
            } else {
                await superadminService.createTeam(salonId, payload);
                showToast('Équipe créée.', 'success');
            }
            setTeamDialogOpen(false);
            await loadSalonData();
        } catch (err) {
            console.error("Failed to save team", err);
            setError("Erreur lors de l'enregistrement de l'équipe");
        } finally {
            setSavingTeam(false);
        }
    };

    const handleOpenReset = (employee) => {
        setResetUser(employee);
        setResetError('');
        setResetSuccess('');
        setResetOpen(true);
    };

    const handleSubmitReset = async () => {
        if (!resetUser?.user_id) return;
        try {
            setResetLoading(true);
            setResetError('');
            setResetSuccess('');
            const res = await superadminService.resetUserPassword(resetUser.user_id);
            setResetSuccess(res?.data?.message || 'Email de réinitialisation envoyé.');
        } catch (err) {
            console.error("Failed to reset password", err);
            setResetError(err.response?.data?.detail || "Erreur lors de la réinitialisation");
        } finally {
            setResetLoading(false);
        }
    };

    const handleOpenEmployeeDialog = (employee = null, preAssignedTeamId = null) => {
        setError('');
        setActiveStep(0);

        const salonHours = (salonData?.opening_hours && Object.keys(salonData.opening_hours).length > 0)
            ? salonData.opening_hours
            : DEFAULT_OPENING_HOURS;

        if (employee) {
            console.log("[SalonTeam] Editing employee:", employee);
            setEditingEmployee(employee);

            // Map existing schedules or initialize defaults
            const existingSchedules = Array.isArray(employee.schedules) ? employee.schedules : [];
            console.log("[SalonTeam] Existing schedules:", existingSchedules);

            const initialSchedules = DAYS_OF_WEEK.map(day => {
                const dayHours = salonHours[day.salonKey];
                const isSalonClosed = !dayHours || dayHours.ouverture === 'fermé' || dayHours.ouverture === 'Fermé';
                const found = existingSchedules.find(s => s.day_of_week === day.id);

                return {
                    day_of_week: day.id,
                    start_time: found ? found.start_time.substring(0, 5) : (isSalonClosed ? '09:00' : dayHours.ouverture),
                    end_time: found ? found.end_time.substring(0, 5) : (isSalonClosed ? '18:00' : dayHours.fermeture),
                    enabled: !!found && !isSalonClosed,
                    salonKey: day.salonKey
                };
            });

            setEmployeeForm({
                first_name: employee.first_name || '',
                last_name: employee.last_name || '',
                email: employee.email || '',
                phone: employee.phone || '',
                password: '',
                has_access: employee.has_access ?? true,
                is_active: employee.is_active ?? true,
                team_ids: employee.teams?.map(t => t.team_id) || [],
                schedules: initialSchedules
            });
            setActiveTab(0);
        } else {
            setEditingEmployee(null);

            // Initialize schedules based on salon opening hours
            const initialSchedules = DAYS_OF_WEEK.map(day => {
                const dayHours = salonHours[day.salonKey];
                const isClosed = !dayHours || dayHours.ouverture === 'fermé' || dayHours.ouverture === 'Fermé';

                return {
                    day_of_week: day.id,
                    start_time: isClosed ? '09:00' : dayHours.ouverture,
                    end_time: isClosed ? '18:00' : dayHours.fermeture,
                    enabled: !isClosed,
                    salonKey: day.salonKey // Store for validation
                };
            });

            setEmployeeForm({
                first_name: '',
                last_name: '',
                email: '',
                phone: '',
                password: '',
                has_access: true,
                team_ids: preAssignedTeamId ? [preAssignedTeamId] : [],
                schedules: initialSchedules
            });
        }
        setEmployeeDialogOpen(true);
    };

    const handleSaveEmployee = async () => {
        try {
            setSavingEmployee(true);
            setError('');

            const salonHours = (salonData?.opening_hours && Object.keys(salonData.opening_hours).length > 0)
                ? salonData.opening_hours
                : DEFAULT_OPENING_HOURS;

            // Validation and Formatting
            const schedules = [];
            for (const s of employeeForm.schedules) {
                if (s.enabled) {
                    const dayInfo = DAYS_OF_WEEK.find(d => d.id === s.day_of_week);
                    const salonDayHours = salonHours[dayInfo.salonKey];

                    if (!salonDayHours || salonDayHours.ouverture === 'fermé' || salonDayHours.ouverture === 'Fermé') {
                        setError(`Le salon est fermé le ${dayInfo.label}.`);
                        setSavingEmployee(false);
                        return;
                    }

                    if (!isWithinSalonHours(s.start_time, s.end_time, salonDayHours.ouverture, salonDayHours.fermeture)) {
                        setError(`Les horaires du ${dayInfo.label} doivent être compris entre ${salonDayHours.ouverture} et ${salonDayHours.fermeture}.`);
                        setSavingEmployee(false);
                        return;
                    }

                    if (s.start_time === s.end_time) {
                        setError(`L'heure de fin doit être différente de l'heure de début pour le ${dayInfo.label}.`);
                        setSavingEmployee(false);
                        return;
                    }

                    schedules.push({
                        day_of_week: s.day_of_week,
                        start_time: s.start_time,
                        end_time: s.end_time
                    });
                }
            }

            const payload = { ...employeeForm, schedules };
            if (editingEmployee) {
                delete payload.password; // Don't send empty password on update
                // Schedules are now handled in the update serializer
                await superadminService.updateEmployee(salonId, editingEmployee.team_member_id, payload);
                showToast('Employé mis à jour.', 'success');
            } else {
                if (!payload.has_access) {
                    delete payload.password;
                }
                await superadminService.createEmployee(salonId, payload);
                showToast('Employé ajouté.', 'success');
            }

            setEmployeeDialogOpen(false);
            await loadSalonData();
        } catch (err) {
            console.error("Failed to save employee", err);
            setError(err.response?.data?.detail || "Erreur lors de l'enregistrement de l'employé");
        } finally {
            setSavingEmployee(false);
        }
    };

    const handleScheduleChange = (dayId, field, value) => {
        setEmployeeForm(prev => ({
            ...prev,
            schedules: prev.schedules.map(s =>
                s.day_of_week === dayId ? { ...s, [field]: value } : s
            )
        }));
    };

    const generateTimeSlots = (start, end, excludeEnd = false) => {
        const slots = [];
        const startMinutes = timeToMinutes(start);
        const normalizedEndMinutes = normalizeInterval(start, end, { allowEqualAsFullDay: true }).end;

        for (let current = startMinutes; current <= normalizedEndMinutes; current += 15) {
            const normalized = current % (24 * 60);
            const hour = String(Math.floor(normalized / 60)).padStart(2, '0');
            const minute = String(normalized % 60).padStart(2, '0');
            const timeString = `${hour}:${minute}`;
            if (excludeEnd && current === normalizedEndMinutes) break;
            slots.push(timeString);
        }
        return slots;
    };

    const getEndTimeOptions = (salonOpen, salonClose, selectedStart) => {
        return generateTimeSlots(salonOpen, salonClose).filter((slot) => compareClockTimes(selectedStart, slot) > 0);
    };

    // Grouping Logic
    const groupedEmployees = {};
    const unassignedEmployees = [];
    const salonTeams = salonData?.teams || [];

    salonTeams.forEach(team => {
        groupedEmployees[team.team_id] = { ...team, members: [] };
    });

    employees.forEach(emp => {
        if (emp.teams && emp.teams.length > 0) {
            emp.teams.forEach(team => {
                if (groupedEmployees[team.team_id]) {
                    groupedEmployees[team.team_id].members.push(emp);
                }
            });
        } else {
            unassignedEmployees.push(emp);
        }
    });

    const renderEmployeeList = (memberList) => (
        <List disablePadding>
            {memberList.map((employee) => (
                <ListItem
                    key={employee.team_member_id}
                    secondaryAction={
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {(employee?.has_access ?? true) && employee?.email && (
                                <IconButton onClick={() => handleOpenReset(employee)} title="Réinitialiser le mot de passe">
                                    <LockResetIcon color="warning" />
                                </IconButton>
                            )}
                            <IconButton onClick={() => handleOpenEmployeeDialog(employee)}>
                                <EditIcon color="primary" />
                            </IconButton>
                            <IconButton onClick={() => handleRequestDeleteEmployee(employee.team_member_id)} disabled={deleting === employee.team_member_id}>
                                <DeleteIcon color="error" />
                            </IconButton>
                        </Box>
                    }
                    sx={{
                        borderBottom: `1px solid ${theme.colors.border.light}`,
                        '&:last-child': { borderBottom: 'none' },
                        py: 2,
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' }
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <ListItemAvatar>
                            <Avatar sx={{ bgcolor: theme.colors.primary[100], color: theme.colors.primary[700], fontWeight: 700 }}>
                                {employee.first_name?.[0]}{employee.last_name?.[0]}
                            </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                            primary={
                                <Typography variant="subtitle1" fontWeight={700} color={theme.colors.text.primary}>
                                    {employee.first_name} {employee.last_name}
                                </Typography>
                            }
                            secondary={
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 0, sm: 1 }, alignItems: { xs: 'flex-start', sm: 'center' }, mt: 0.5 }}>
                                    <Typography variant="body2" color={theme.colors.text.secondary} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                        {employee.email}
                                    </Typography>
                                    {employee.phone && (
                                        <>
                                            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' }, height: 12, my: 'auto' }} />
                                            <Typography variant="body2" color={theme.colors.text.secondary} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                {employee.phone}
                                            </Typography>
                                        </>
                                    )}
                                </Box>
                            }
                        />
                    </Box>

                    {/* Mobile Only Actions */}
                    <Box sx={{
                        display: { xs: 'flex', sm: 'none' },
                        width: '100%',
                        justifyContent: 'flex-end',
                        gap: 1.5,
                        mt: 1.5,
                        pt: 1,
                        borderTop: `1px dashed ${theme.colors.border.light}`
                    }}>
                        <IconButton
                            size="small"
                            onClick={() => handleOpenReset(employee)}
                            sx={{ bgcolor: theme.colors.warning[50], color: theme.colors.warning[600] }}
                        >
                            <LockResetIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={() => handleOpenEmployeeDialog(employee)}
                            sx={{ bgcolor: theme.colors.primary[50], color: theme.colors.primary[600] }}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={() => handleRequestDeleteEmployee(employee.team_member_id)}
                            disabled={deleting === employee.team_member_id}
                            sx={{ bgcolor: theme.colors.error[50], color: theme.colors.error[600] }}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </ListItem>
            ))}
        </List>
    );

    return (
        <Box>
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: 2,
                mb: 3
            }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: theme.colors.text.primary, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                    Équipe et Membres ({employees.length})
                </Typography>
                <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, width: { xs: '100%', sm: 'auto' } }}>
                    <Button
                        variant="outlined"
                        startIcon={<GroupAddIcon />}
                        onClick={() => handleOpenTeamDialog()}
                        sx={{
                            fontWeight: 700,
                            borderRadius: theme.borderRadius.lg,
                            textTransform: 'none',
                            px: { xs: 1.5, sm: 2 },
                            flex: { xs: 1, sm: 'none' }
                        }}
                    >
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Nouvelle </Box>Équipe
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<PersonAddIcon />}
                        onClick={() => handleOpenEmployeeDialog()}
                        sx={{
                            fontWeight: 700,
                            borderRadius: theme.borderRadius.lg,
                            textTransform: 'none',
                            px: { xs: 1.5, sm: 2 },
                            flex: { xs: 1, sm: 'none' }
                        }}
                    >
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Ajouter un </Box>Employé
                    </Button>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {salonTeams.map(team => {
                    const teamGroup = groupedEmployees[team.team_id];
                    const members = teamGroup?.members || [];

                    return (
                        <Accordion key={team.team_id} defaultExpanded sx={{ boxShadow: theme.boxShadow.sm, borderRadius: theme.borderRadius.lg + ' !important', '&:before': { display: 'none' } }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <WorkIcon color="action" />
                                        <Typography variant="h6" fontWeight={700}>
                                            {team.name}
                                        </Typography>
                                        <Chip label={`${members.length} membres`} size="small" sx={{ fontWeight: 600, bgcolor: theme.colors.primary[50], color: theme.colors.primary[700] }} />
                                    </Box>
                                    <Box sx={{ mr: 2, display: 'flex', gap: 1 }} onClick={(e) => e.stopPropagation()}>
                                        <IconButton size="small" onClick={() => handleOpenEmployeeDialog(null, team.team_id)} title="Ajouter un membre à cette équipe">
                                            <AddIcon color="primary" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleOpenTeamDialog(team)} title="Modifier l'équipe">
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleRequestDeleteTeam(team.team_id)} title="Supprimer l'équipe">
                                            <DeleteIcon fontSize="small" color="error" />
                                        </IconButton>
                                    </Box>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0 }}>
                                {members.length > 0 ? (
                                    renderEmployeeList(members)
                                ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                                        Aucun membre dans cette équipe.
                                    </Typography>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    );
                })}

                {unassignedEmployees.length > 0 && (
                    <Accordion defaultExpanded sx={{ boxShadow: theme.boxShadow.sm, borderRadius: theme.borderRadius.lg + ' !important', '&:before': { display: 'none' } }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="h6" fontWeight={700} color="text.secondary">
                                    Non assigné(s)
                                </Typography>
                                <Chip label={`${unassignedEmployees.length}`} size="small" />
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0 }}>
                            {renderEmployeeList(unassignedEmployees)}
                        </AccordionDetails>
                    </Accordion>
                )}

                {employees.length === 0 && (
                    <Box
                        sx={{
                            p: 6,
                            textAlign: 'center',
                            bgcolor: theme.colors.background.secondary,
                            borderRadius: theme.borderRadius.xl,
                            border: `1px dashed ${theme.colors.border.light}`
                        }}
                    >
                        <Typography variant="body1" sx={{ color: theme.colors.text.secondary, fontWeight: 500 }}>
                            Aucun membre dans l'équipe.
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Team Dialog */}
            <Dialog open={teamDialogOpen} onClose={() => setTeamDialogOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontWeight: 800 }}>{editingTeam ? 'Modifier l\'Équipe' : 'Nouvelle Équipe'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Nom de l'équipe"
                            fullWidth
                            value={teamName}
                            onChange={(e) => setTeamName(e.target.value)}
                        />
                        <TextField
                            label="Description"
                            fullWidth
                            multiline
                            rows={3}
                            value={teamDescription}
                            onChange={(e) => setTeamDescription(e.target.value)}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setTeamDialogOpen(false)}>Annuler</Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveTeam}
                        disabled={!teamName || savingTeam}
                    >
                        {savingTeam ? <CircularProgress size={24} /> : 'Enregistrer'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* 2-Step Employee Dialog */}
            <Dialog open={employeeDialogOpen} onClose={() => setEmployeeDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ fontWeight: 800 }}>
                    {editingEmployee ? 'Modifier l\'Employé' : 'Ajouter un Employé'}
                </DialogTitle>
                <DialogContent>
                    {editingEmployee ? (
                        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                            <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="fullWidth">
                                <Tab label="Informations" />
                                <Tab label="Planning" />
                            </Tabs>
                        </Box>
                    ) : (
                        <Stepper activeStep={activeStep} sx={{ py: 3 }}>
                            <Step>
                                <StepLabel>Compte</StepLabel>
                            </Step>
                            <Step>
                                <StepLabel>Planning</StepLabel>
                            </Step>
                        </Stepper>
                    )}

                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    {(editingEmployee ? activeTab === 0 : activeStep === 0) ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField
                                    label="Prénom"
                                    fullWidth
                                    value={employeeForm.first_name}
                                    onChange={(e) => setEmployeeForm({ ...employeeForm, first_name: e.target.value })}
                                />
                                <TextField
                                    label="Nom"
                                    fullWidth
                                    value={employeeForm.last_name}
                                    onChange={(e) => setEmployeeForm({ ...employeeForm, last_name: e.target.value })}
                                />
                            </Box>
                            <TextField
                                label="Email"
                                type="email"
                                fullWidth
                                value={employeeForm.email}
                                onChange={(e) => setEmployeeForm({ ...employeeForm, email: e.target.value })}
                            />
                            <TextField
                                label="Téléphone"
                                fullWidth
                                value={employeeForm.phone}
                                onChange={(e) => setEmployeeForm({ ...employeeForm, phone: e.target.value })}
                            />
                            {!editingEmployee && (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={!employeeForm.has_access}
                                            onChange={(e) => setEmployeeForm({ ...employeeForm, has_access: !e.target.checked })}
                                            color="primary"
                                        />
                                    }
                                    label={employeeForm.has_access ? "Avec accès" : "Sans accès"}
                                />
                            )}
                            {!editingEmployee && (
                                employeeForm.has_access ? (
                                    <TextField
                                        label="Mot de passe"
                                        type="password"
                                        fullWidth
                                        value={employeeForm.password}
                                        onChange={(e) => setEmployeeForm({ ...employeeForm, password: e.target.value })}
                                    />
                                ) : null
                            )}
                            <FormControl fullWidth>
                                <InputLabel>Équipes</InputLabel>
                                <Select
                                    multiple
                                    value={employeeForm.team_ids}
                                    onChange={(e) => setEmployeeForm({ ...employeeForm, team_ids: e.target.value })}
                                    input={<OutlinedInput label="Équipes" />}
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((value) => {
                                                const team = salonData?.teams?.find(t => t.team_id === value);
                                                return <Chip key={value} label={team?.name || value} size="small" />;
                                            })}
                                        </Box>
                                    )}
                                >
                                    {salonData?.teams?.map((team) => (
                                        <MenuItem key={team.team_id} value={team.team_id}>
                                            {team.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {editingEmployee && (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={employeeForm.is_active}
                                            onChange={(e) => setEmployeeForm({ ...employeeForm, is_active: e.target.checked })}
                                            color="primary"
                                        />
                                    }
                                    label={employeeForm.is_active ? "Compte Actif" : "Compte Inactif"}
                                    sx={{ mt: 1 }}
                                />
                            )}
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Définissez les horaires de travail. Ils doivent être compris dans les heures d'ouverture du salon.
                            </Typography>
                            {DAYS_OF_WEEK.map((day) => {
                                const schedule = employeeForm.schedules.find(s => s.day_of_week === day.id);
                                const salonHours = (salonData?.opening_hours && Object.keys(salonData.opening_hours).length > 0)
                                    ? salonData.opening_hours
                                    : DEFAULT_OPENING_HOURS;
                                const salonDayHours = salonHours[day.salonKey];
                                const isSalonClosed = !salonDayHours || salonDayHours.ouverture === 'fermé' || salonDayHours.ouverture === 'Fermé';

                                if (isSalonClosed) return null;

                                return (
                                    <Box key={day.id} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <FormControlLabel
                                            sx={{ minWidth: 120 }}
                                            control={
                                                <Switch
                                                    checked={schedule.enabled}
                                                    onChange={(e) => handleScheduleChange(day.id, 'enabled', e.target.checked)}
                                                />
                                            }
                                            label={day.label}
                                        />
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <FormControl size="small" sx={{ minWidth: 100 }}>
                                                <Select
                                                    value={schedule.start_time}
                                                    disabled={!schedule.enabled}
                                                    onChange={(e) => {
                                                        const newStart = e.target.value;
                                                        handleScheduleChange(day.id, 'start_time', newStart);
                                                        // Ensure end_time is still after start_time
                                                        if (compareClockTimes(newStart, schedule.end_time) <= 0) {
                                                            const nextAvailable = getEndTimeOptions(salonDayHours.ouverture, salonDayHours.fermeture, newStart)[0];
                                                            if (nextAvailable) handleScheduleChange(day.id, 'end_time', nextAvailable);
                                                        }
                                                    }}
                                                >
                                                    {generateTimeSlots(salonDayHours.ouverture, salonDayHours.fermeture, true).map(slot => (
                                                        <MenuItem key={slot} value={slot}>{slot}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            <Typography>à</Typography>
                                            <FormControl size="small" sx={{ minWidth: 100 }}>
                                                <Select
                                                    value={schedule.end_time}
                                                    disabled={!schedule.enabled}
                                                    onChange={(e) => handleScheduleChange(day.id, 'end_time', e.target.value)}
                                                >
                                                    {getEndTimeOptions(salonDayHours.ouverture, salonDayHours.fermeture, schedule.start_time)
                                                        .map(slot => (
                                                            <MenuItem key={slot} value={slot}>{slot}</MenuItem>
                                                        ))}
                                                </Select>
                                            </FormControl>
                                        </Box>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', ml: 'auto' }}>
                                            (Salon: {salonDayHours.ouverture} - {salonDayHours.fermeture})
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setEmployeeDialogOpen(false)}>Annuler</Button>
                    {editingEmployee ? (
                        <Button
                            variant="contained"
                            onClick={handleSaveEmployee}
                            disabled={savingEmployee}
                        >
                            {savingEmployee ? <CircularProgress size={24} /> : 'Enregistrer les modifications'}
                        </Button>
                    ) : (
                        <>
                            {activeStep === 0 ? (
                                <Button
                                    variant="contained"
                                    disabled={!employeeForm.first_name || !employeeForm.last_name || (employeeForm.has_access && (!employeeForm.email || !employeeForm.password))}
                                    onClick={() => setActiveStep(1)}
                                >
                                    Suivant
                                </Button>
                            ) : (
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button onClick={() => setActiveStep(0)}>Retour</Button>
                                    <Button
                                        variant="contained"
                                        onClick={handleSaveEmployee}
                                        disabled={savingEmployee}
                                    >
                                        {savingEmployee ? <CircularProgress size={24} /> : 'Créer l\'employé'}
                                    </Button>
                                </Box>
                            )}
                        </>
                    )}
                </DialogActions>
            </Dialog>
            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ ...deleteConfirm, open: false })} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 800 }}>Confirmer la suppression</DialogTitle>
                <DialogContent>
                    <Typography>
                        {deleteConfirm.type === 'team'
                            ? "Voulez-vous vraiment supprimer cette équipe ? Les membres ne seront pas supprimés."
                            : "Voulez-vous vraiment retirer cet employé du salon ?"
                        }
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setDeleteConfirm({ ...deleteConfirm, open: false })}>Annuler</Button>
                    <Button variant="contained" color="error" onClick={handleConfirmDelete} disabled={deleting !== null}>
                        {deleting ? <CircularProgress size={24} color="inherit" /> : 'Supprimer'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reset Password Dialog */}
            <Dialog open={resetOpen} onClose={() => setResetOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontWeight: 800 }}>Réinitialisation mot de passe</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography>
                            Voulez-vous envoyer un email de réinitialisation de mot de passe à <b>{resetUser?.first_name} {resetUser?.last_name}</b> ({resetUser?.email}) ?
                        </Typography>
                        {resetError && <Alert severity="error">{resetError}</Alert>}
                        {resetSuccess && <Alert severity="success">{resetSuccess}</Alert>}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setResetOpen(false)}>Fermer</Button>
                    {!resetSuccess && (
                        <Button
                            variant="contained"
                            onClick={handleSubmitReset}
                            disabled={resetLoading}
                        >
                            {resetLoading ? <CircularProgress size={24} /> : 'Envoyer'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={handleCloseToast}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: '100%', fontWeight: 600, borderRadius: theme.borderRadius.md }} variant="filled">
                    {toast.message}
                </Alert>
            </Snackbar>
        </Box >
    );
};

export default SalonTeam;
